import { useContext, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Grid,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import NextApptTable from './NextApptTable'; 
import NextApptCalendar from './NextApptCalendar';
import PropTypes from 'prop-types';
import { useBebackApi } from '../hooks/useBebackApi';
import StoreContext from "../context/StoreContext";
import { useCallback } from 'react';
import FeedbackContext from '../context/FeedbackContext';

const NextApptModal = ({open, onClose, woData, setOpenErrorPopup, setErrorMessage}) => {
  const { getNextApptData, createNextAppt } = useBebackApi()
  const [searchedValue, setSearchedValue] = useState('');
  const { storeId, time_zone } = useContext(StoreContext);
  const [calendarValue, setCalendarValue] = useState(null); // DateTime.now()
  const [apptData, setApptData] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [subService, setSubService] = useState(null);
  const [sendAppt, setSendAppt] = useState(false);
  const [requiredFields, setRequiredFields] = useState(false);
  const { setIsLoading } = useContext(FeedbackContext);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const res = await getNextApptData(+storeId, calendarValue.toFormat('yyyyMMdd'))
    if (res.error){
      setErrorMessage('Service unavailable. Please try again later.');
      setOpenErrorPopup(true);
      setApptData([]);
      onClose();
    } else {
      const newRes = res.map(item => {  return {...item, services: [...new Map(item.services.map(item => [item['service_id'], item])).values()]}})
      
      setApptData(newRes);
    }
    setIsLoading(false);
  },[calendarValue, getNextApptData, onClose, setErrorMessage, setIsLoading, setOpenErrorPopup, storeId])

  useEffect(() => {
    if (calendarValue) {
      setSelectedService(null)
      setSubService(null)
      if(+storeId !== 1) {
        fetchData();
      }
    }
  }, [calendarValue, fetchData, storeId]);

  useEffect(() => {
    if(calendarValue !== null && selectedService !== null && subService !== null) {
      setRequiredFields(false)
    } else if(sendAppt && (calendarValue === null || selectedService === null || subService === null)) {
      setRequiredFields(true)
    }
  },[calendarValue, selectedService, subService, sendAppt])

  const handleCreateAppt = async () => {
    try {
      setIsLoading(true);
      setSendAppt(true);
      const serviceDetails = apptData[selectedService];
      const subServiceDetails = serviceDetails.services.filter((o) => {return o.service_id === subService.value})[0];
      delete serviceDetails.services;
      serviceDetails.service_id = subServiceDetails.service_id;
      serviceDetails.time_zone = time_zone;
      serviceDetails.service_name = subServiceDetails.title;

      const createApptRes = await createNextAppt(woData.WO, serviceDetails)

      if (createApptRes?.NextAppointmentId) {
        woData.NextAppointmentId = createApptRes.NextAppointmentId;
        setIsLoading(false);
        onClose(); //close the Next Appt modal
      }

      if (createApptRes?.isInvalid) {
        setErrorMessage(createApptRes?.message);
        setSelectedService(null)
        setSubService(null)
        fetchData();
        setOpenErrorPopup(true);
        setIsLoading(false)
      }

      if(createApptRes === undefined || createApptRes?.error) {
        setErrorMessage('Error creating appointment, please try again')
        setSelectedService(null)
        setSubService(null)
        fetchData();
        setOpenErrorPopup(true);
        setIsLoading(false)
      }

    } catch (error) {
      console.log(`Error during handleCreateAppt: `, error)
      setRequiredFields(true)
      setIsLoading(false)
    }
  }

  function styledField(field) {
    if(field === null && sendAppt) {
      return '1px solid #dc3545'
    }
    return '';
  }

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth='md'
      fullWidth={true}
      PaperProps={{
        sx: {
          maxHeight: '600px'
        },
      }}
    >
      <DialogContent sx={{ display: "grid" }}>
        <div className='flex justify-end'>
          <IconButton
            aria-label='close modal'
            onClick={onClose}
            color='error'
          >
            <CloseIcon style={{ padding: 0 }}/>
          </IconButton>
        </div>
        <Grid container spacing={2}>
          <Grid item xs={5} sx={{border: styledField(calendarValue), borderRadius: '.25em'}}>
            <NextApptCalendar value={calendarValue} setValue={setCalendarValue} readOnly={false}/>
          </Grid>
          <Grid container item xs={7}> 
            <Grid item xs={12} sx={{border: styledField(selectedService), borderRadius: '.25em'}}>
              <NextApptTable
                apptData={apptData}
                setSearchedValue={setSearchedValue}
                searchedValue={searchedValue}
                selected={selectedService}
                setSelected={setSelectedService}
                setSubService={setSubService}
                subService={subService}
                styledField={styledField}
              />
            </Grid>
            { requiredFields &&
              <Grid item xs={6} sx={{alignSelf: 'center', color:'#dc3545', fontSize: 14, textAlign:'center'}}>
                Please fill in the required fields
              </Grid>
            }
          </Grid>
          <Grid container item xs={12} spacing={2}>
            <Grid item xs={4}>
              <TextField 
                fullWidth
                label="Name" 
                variant="standard"
                value={woData?.Name}
                InputProps={{
                  readOnly: true,
                }} 
              />
            </Grid>
            <Grid item xs={4}>
              <TextField 
                fullWidth
                label="Phone" 
                variant="standard" 
                value={woData?.Phone}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField 
                fullWidth
                label="Email" 
                variant="standard"
                value={(woData?.Email && woData.Email !== null) ? woData.Email : ''}
                InputProps={{
                  readOnly: true,
                }} 
              />
            </Grid>
            <Grid item xs={3}>
              <TextField 
                fullWidth
                label="Year" 
                variant="standard"
                value={(woData?.VehicleYear && woData.VehicleYear !== null) ? woData.VehicleYear : ''}
                InputProps={{
                  readOnly: true,
                }} 
              />
            </Grid>
            <Grid item xs={3}>
              <TextField 
                fullWidth
                label="Make" 
                variant="standard"
                value={(woData?.VehicleMake && woData.VehicleMake !== null) ? woData.VehicleMake : ''}
                InputProps={{
                  readOnly: true,
                }} 
              />
            </Grid>
            <Grid item xs={3}>
              <TextField 
                fullWidth
                label="Model" 
                variant="standard"
                value={(woData?.VehicleModel && woData.VehicleModel !== null) ? woData.VehicleModel : ''}
                InputProps={{
                  readOnly: true,
                }} 
              />
            </Grid>
            <Grid item xs={3} sx={{marginY: 'auto'}}>
              <Button type="submit" variant="contained" fullWidth className='button red' onClick={() => handleCreateAppt()}>
                Submit
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

NextApptModal.propTypes = {
  open: PropTypes.bool, 
  onClose: PropTypes.func,
  woData: PropTypes.object,
  setOpenErrorPopup: PropTypes.func,
  setErrorMessage: PropTypes.func
}

export default NextApptModal;
